import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./HotelSearchBox.module.css";

function HotelSearchBox({
  hotels = [],
  selectedHotels = [],
  selectedHotel = null,
  onSelect,
  onRemove,
  placeholder = "Search hotel name...",
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!selectedHotel) {
      inputRef.current?.focus();
    }
  }, [selectedHotel]);



  const selectedIds = useMemo(
    () =>
      new Set(
        selectedHotels.map((hotel) => hotel.id)
      ),
    [selectedHotels]
  );



  const suggestions = useMemo(() => {
    const value = query.trim().toLowerCase();

    if (!value) return [];

    return hotels
      .filter(
        (hotel) =>
          !selectedIds.has(hotel.id) &&
          hotel.name
            ?.toLowerCase()
            .includes(value)
      )
      .slice(0, 5);
  }, [hotels, query, selectedIds]);



  const selectHotel = (hotel) => {
    onSelect?.(hotel);

    setQuery("");
    setActiveIndex(-1);
  };


  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev < suggestions.length - 1
          ? prev + 1
          : 0
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev > 0
          ? prev - 1
          : suggestions.length - 1
      );
    }

    if (e.key === "Enter") {
      e.preventDefault();

      if (activeIndex >= 0) {
        selectHotel(
          suggestions[activeIndex]
        );
      }
    }

    if (e.key === "Escape") {
      setActiveIndex(-1);
    }
  };

  return (
    <div className={styles.wrapper}>
      {selectedHotel ? (
        /* =========================
           SELECTED HOTEL
        ========================= */

        <div className={styles.selectedHotel}>
          {selectedHotel.featured_image && (
            <img
              src={selectedHotel.featured_image}
              alt={
                selectedHotel.name ||
                "Hotel"
              }
              className={styles.hotelImage}
            />
          )}

          <div className={styles.hotelInfo}>
            <strong>
              {selectedHotel.name}
            </strong>

            {selectedHotel.city && (
              <span>
                {selectedHotel.city}
                {selectedHotel.state
                  ? `, ${selectedHotel.state}`
                  : ""}
              </span>
            )}
          </div>

          <button
            type="button"
            className={styles.removeButton}
            onClick={() =>
              onRemove?.(
                selectedHotel.id
              )
            }
            aria-label={`Remove ${selectedHotel.name}`}
          >
            ×
          </button>
        </div>
      ) : (
   
        <>
          <div className={styles.searchBox}>
            <span
              className={styles.searchIcon}
            >
              ⌕
            </span>

            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={placeholder}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* =========================
             SUGGESTIONS
          ========================= */}

          {suggestions.length > 0 && (
            <div
              className={styles.suggestions}
            >
              {suggestions.map(
                (hotel, index) => (
                  <button
                    key={hotel.id}
                    type="button"
                    className={`${
                      styles.suggestion
                    } ${
                      activeIndex === index
                        ? styles.active
                        : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();

                      selectHotel(hotel);
                    }}
                    onMouseEnter={() =>
                      setActiveIndex(index)
                    }
                  >
                    {hotel.featured_image && (
                      <img
                        src={
                          hotel.featured_image
                        }
                        alt={
                          hotel.name ||
                          "Hotel"
                        }
                      />
                    )}

                    <span>
                      <strong>
                        {hotel.name}
                      </strong>

                      {hotel.city && (
                        <small>
                          {hotel.city}
                          {hotel.state
                            ? `, ${hotel.state}`
                            : ""}
                        </small>
                      )}
                    </span>
                  </button>
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HotelSearchBox;